import { Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";

import { IMAGES } from "@APP/assets";
import { useAppSelector } from "@APP/redux";
import { MOBILE_AVATAR, MOBILE_BADGE, MOBILE_GUTTER } from "@APP/styles/mobile";

/**
 * "Doms Company" -> "DC". Used as the merchant avatar fallback when the
 * organisation has no logo of its own.
 */
export const getInitials = (name?: string | null) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");

const useStyles = makeStyles((theme) => ({
  // The LBG designs drop any header bar on every screen: the merchant sits in a
  // white circle on the page background, with the Lloyds horse in a green badge
  // on the right. Sizes scale down below the `sm` breakpoint.
  bar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: "24px 5vw",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      padding: MOBILE_GUTTER,
    },
  },
  avatar: {
    width: 92,
    height: 92,
    flexShrink: 0,
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: MOBILE_AVATAR,
      height: MOBILE_AVATAR,
    },
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  avatarInitials: {
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      // Scaled to the 76px mobile avatar (36 × 76/92).
      fontSize: 30,
    },
  },
  secure: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
    color: theme.palette.text.primary,
  },
  secureText: {
    whiteSpace: "nowrap",
    fontSize: 16,
    [theme.breakpoints.down("sm")]: {
      // "Safe & Secure" sits in a 20px line box on the mobile Figma (≈14px).
      fontSize: 14,
    },
  },
  badge: {
    width: 55,
    height: 55,
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: theme.palette.success.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: MOBILE_BADGE,
      height: MOBILE_BADGE,
    },
  },
  badgeIcon: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
}));

const Header = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  const { orgLogo, senderData } = useAppSelector((state) => state.rtp);
  const merchantName = senderData?.businessContact?.name ?? senderData?.name;
  const initials = getInitials(merchantName);

  return (
    <Box component="header" className={styles.bar} id="pageHeaderBox">
      <Box className={styles.avatar} id="pageHeaderMerchantAvatar">
        {orgLogo ? (
          <img
            src={orgLogo}
            className={styles.avatarImage}
            alt={merchantName ?? ""}
            id="pageHeaderMerchantLogo"
          />
        ) : (
          <Typography
            component="span"
            className={styles.avatarInitials}
            aria-hidden={!initials}
            id="pageHeaderMerchantInitials">
            {initials}
          </Typography>
        )}
      </Box>
      <Typography className="hidden" component="h1" id="pageHeaderProductName">
        {t("ProductName")}
      </Typography>
      <Box className={styles.secure} id="pageHeaderSecure">
        <Typography component="span" className={styles.secureText}>
          Safe &amp; Secure
        </Typography>
        <Box className={styles.badge} id="pageHeaderBrandBadge">
          <img src={IMAGES.APP_LOGO} className={styles.badgeIcon} alt={t("ProductName")} />
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
